<template>
    <EditComponent
        dialog-id="editCertificationDialog"
        open-dialog-event="certifications:edit-mode"
        updated-event="certifications:updated"
        :open-dialog-post-function="initCertification"
        :confirm-function="handleDialogConfirm"
        :track-section-opened="EditionOpenedSection.CERTIFICATION"
        :track-existing-element="true"
    >
        <template #edit-header>{{ t('profile.section.certifications.title') }}</template>
        <template #edit-body>
            <VJoyWrapper v-if="!isLoading && !hasError" direction="column" align="stretch" row-gap="4">
                <VJoyInput
                    v-model="element.title"
                    :required="true"
                    :invalid="r$.title.$error"
                    name="certification-title"
                    :data-testid="`profile-edition-section-certification-title-${idOrNew}`"
                    :placeholder="t('profile.new.certification.title')"
                >
                    {{ t('profile.new.certification.title') }}
                </VJoyInput>
                <VJoyFormError v-if="r$.title.$error" :data-testid="`profile-edition-section-certification-title-error-${idOrNew}`">
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>

                <VJoyInput
                    v-model="element.organization"
                    :required="true"
                    :invalid="r$.organization.$error"
                    name="certification-organization"
                    :data-testid="`profile-edition-section-certification-organization-${idOrNew}`"
                    :placeholder="t('profile.new.certification.title')"
                >
                    {{ t('profile.new.certification.organization') }}
                </VJoyInput>
                <VJoyFormError v-if="r$.organization.$error" :data-testid="`profile-edition-section-certification-organization-error-${idOrNew}`">
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>

                <div class="profile-edition-section-certification__list-item-graduation">
                    <VJoyInput
                        v-model="element.graduationYear"
                        :required="true"
                        :invalid="r$.graduationYear.$error"
                        name="certification-graduation-year"
                        :data-testid="`profile-edition-section-certification-graduation-year-${idOrNew}`"
                        :disabled="isCurrent()"
                        type="number"
                        :placeholder="t('profile.new.certification.graduation.year')"
                    >
                        {{ t('profile.new.certification.graduation.year') }}
                    </VJoyInput>

                    <VJoyCheckbox
                        v-model:checked="element.current"
                        :data-testid="`profile-edition-section-certification-is-current-job-${idOrNew}`"
                        :data-is-checked="element.current"
                        class="profile-edition-section-certification__list-item-graduation-checkbox"
                        @joy-checkbox-change="updateCurrentCertification"
                    >
                        {{ t('newexperience.currentJob') }}
                    </VJoyCheckbox>
                </div>
                <VJoyFormError
                    v-if="r$.graduationYear.$error"
                    :data-testid="`profile-edition-section-certification-graduation-year-error-${idOrNew}`"
                >
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>

                <VJoyInput
                    v-model="element.link"
                    name="certification-link"
                    :data-testid="`profile-edition-section-certification-link-${idOrNew}`"
                    :placeholder="t('profile.new.certification.link')"
                >
                    {{ t('profile.new.certification.link') }}
                </VJoyInput>
                <VJoyFormError v-if="isNotALink" :key="element.index" :data-testid="`profile-edition-section-certification-form-error-${idOrNew}`">
                    {{ t('form.field.error.message.url', {0: element.link}) }}
                </VJoyFormError>

                <div>
                    <VJoyLabel class="profile-edition-section-certification__skills__label">
                        {{ t('profile.section.certifications.edit.tag.label') }}
                        <div class="profile-edition-section-certification__skills__counter">
                            <CircleProgressBar
                                class="profile-edition-section-certification__skills__circle-progress"
                                :data-testid="`profile-edition-section-certification-skills-counter-${idOrNew}`"
                                :completion-percentage="skillsCounterPercentage"
                                :color="skillsCounterColor"
                            />
                            <div>
                                {{
                                    t('profile.section.skill.set.edition.selectedskills.counter.label', [
                                        `${certificationGlobalSkills.length}`,
                                        `${certificationSkills.max}`,
                                    ])
                                }}
                            </div>
                        </div>
                    </VJoyLabel>
                    <EditGlobalSkills
                        :data-testid="`profile-edition-section-certification-skills-${idOrNew}`"
                        :disabled="hasReachedGlobalSkillsLimit"
                        :model-value="certificationGlobalSkills"
                        :rules-global-skill-label-min-length="skillSetGlobalSkillLabel.min"
                        :rules-global-skill-label-max-length="skillSetGlobalSkillLabel.max"
                        :show-label="false"
                        :show-tag-list="true"
                        :show-occurences="true"
                        @update:model-value="handleEditGlobalSkillsUpdateModelValue"
                    />
                </div>
            </VJoyWrapper>

            <FetchErrorHighlight v-if="hasError" :data-testid="`profile-edition-section-certification-error-${idOrNew}`" />
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {minValue, maxValue, required, integer} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {computed, ref, useTranslation, watch} from '#imports';
    import {isValidUrl} from '@malt/common';
    import {useRegle, validateForm} from '@malt/forms';
    import {pushVJoySnackbar, VJoyCheckbox, VJoyFormError, VJoyInput, VJoyLabel, VJoyWrapper} from '@maltjoy/core-vue';
    import type {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {EditionOpenedSection} from '~/config/TrackingEvents';
    import type {Skill} from '~/openapi';
    import {SkillTypeEnum} from '~/openapi';
    import {useRulesStore} from '~/store/rules/rulesStore';
    import {certificationMapper} from '~/types/infra/CertificationMapper';
    import type {CertificationRequest} from '~/types/infra/CertificationRequest';
    import CircleProgressBar from '@/components/_common/counter/CircleProgressBar.vue';
    import FetchErrorHighlight from '@/components/_common/FetchErrorHighlight.vue';
    import EditComponent from '@/components/edit/EditComponent.vue';
    import EditGlobalSkills from '@/components/edit/skill-set/EditGlobalSkills.vue';
    import {useProfileStore} from '@/store/profileStore';
    import type {Skill as SkillCertification} from '@/store/profileStore';

    const {t} = useTranslation();
    const {updateCertifications} = useProfileStore();
    const {certifications} = storeToRefs(useProfileStore());
    const {certificationSkills, skillSetGlobalSkillLabel} = storeToRefs(useRulesStore());

    const index = ref<number>(0);
    const element = ref<CertificationRequest>(defaultValue());
    const isNotALink = computed<boolean>(() => !!element.value && !!element.value.link && !isValidUrl(element.value.link));
    const certificationGlobalSkills = ref<Skill[]>([]);

    const idOrNew = computed(() => (element.value.id ? `${element.value.id}` : 'new'));
    const hasReachedGlobalSkillsLimit = computed<boolean>(() => certificationGlobalSkills.value.length >= certificationSkills.value.max);
    const hasExceededGlobalSkillsLimit = computed<boolean>(() => certificationGlobalSkills.value.length > certificationSkills.value.max);
    const skillsCounterPercentage = computed(() => {
        const percentage = (certificationGlobalSkills.value.length / certificationSkills.value.max) * 100;
        return percentage <= 100 ? percentage : 100;
    });
    const skillsCounterColor = computed((): string => {
        if (hasExceededGlobalSkillsLimit.value === true) {
            return 'var(--joy-color-primary-70)';
        }
        return 'var(--joy-color-tertiary-50)';
    });

    const isLoading = ref(true);
    const hasError = ref(false);

    const {r$} = useRegle(element, () => ({
        title: {required},
        organization: {required},
        graduationYear: element.value?.current ? {} : {required, integer, minValue: minValue(1900), maxValue: maxValue(new Date().getFullYear())},
    }));

    function handleEditGlobalSkillsUpdateModelValue(modelValue: Skill[]) {
        certificationGlobalSkills.value = modelValue;
    }

    watch(
        certificationGlobalSkills,
        () => {
            element.value!.skills = certificationGlobalSkills.value.map((selectedSkill: Skill) => ({label: selectedSkill.label}));
        },
        {deep: true},
    );

    function defaultValue() {
        return {id: undefined, index: 0, organization: '', link: '', title: '', current: false, skills: []};
    }
    function initCertification(data: {certificationId: number | undefined; entryPoint: EditionOpenedEntryPoint}) {
        certificationGlobalSkills.value = [];
        r$.$reset();

        if (data.certificationId !== undefined) {
            index.value = data.certificationId;

            if (certifications.value?.items[data.certificationId]) {
                element.value = certificationMapper.toRequest(certifications.value.items[data.certificationId], data.certificationId);
                certificationGlobalSkills.value = element.value.skills.map((skill: SkillCertification) => ({
                    id: skill.label,
                    label: skill.label,
                    type: SkillTypeEnum.Global,
                }));
            } else {
                hasError.value = true;
            }
        } else {
            element.value = defaultValue();
        }

        isLoading.value = false;
    }

    function isCurrent(): boolean {
        return !!element.value && element.value.current;
    }

    function updateCurrentCertification(e: CustomEvent) {
        element.value!.current = e.detail;
    }

    async function handleDialogConfirm() {
        const {valid} = await validateForm(r$);
        if (!valid || isNotALink.value) {
            return false;
        }
        try {
            if (element.value.id) {
                await update();
            } else {
                await create();
            }
        } catch (e) {
            pushVJoySnackbar({
                props: {
                    message: t('generic.error.unexpected'),
                    level: 'error',
                },
            });
            return false;
        }
    }

    async function create() {
        const currentCertifications = certifications.value;
        const certificationRequests =
            currentCertifications?.items.map((certification, index) => {
                return certificationMapper.toRequest(certification, index);
            }) || [];
        element.value!.index = certificationRequests.length;
        certificationRequests.push(element.value!);

        await updateCertifications({certifications: certificationRequests});
    }

    async function update() {
        const certificationRequests: CertificationRequest[] = [];

        if (certifications.value && certifications.value.items) {
            certificationRequests.push(
                ...certifications.value.items.map((certification, idx) => {
                    if (idx === index.value) {
                        return element.value!;
                    }

                    return certificationMapper.toRequest(certification, idx);
                }),
            );
        }

        if (certificationRequests) {
            await updateCertifications({certifications: certificationRequests});
        }
    }
</script>

<style lang="scss" scoped>
    .draggable {
        cursor: grab;
        margin-left: auto;
    }

    .profile-edition-section-certification {
        &__skills {
            &__circle-progress {
                line-height: 0;
                display: flex;
                align-items: center;
            }

            &__counter {
                display: flex;
                align-items: center;
            }

            &__label {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }
        }

        &__list {
            &-title {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
            }

            &-item {
                display: flex;
                flex-direction: column;
                width: 99%;

                &-graduation {
                    width: 100%;
                    display: flex;
                    align-items: center;

                    &-checkbox {
                        margin-top: var(--joy-core-spacing-6);
                        margin-left: auto;
                    }
                }
            }

            &-actions {
                display: flex;
                justify-content: flex-end;
                margin-top: var(--joy-core-spacing-5);

                & > joy-button {
                    margin-left: var(--joy-core-spacing-2);
                }
            }
        }
    }
</style>
